/**
 * Per-theme CSS for the chessground wrapper. Each theme overrides:
 *   - the chequer pattern (cg-board background — chessground does NOT tag
 *     squares with `.light`/`.dark` classes; the pattern is drawn entirely
 *     by an inline SVG background on cg-board, same approach as lichess's
 *     own brown/blue/green themes)
 *   - last-move highlight: TWO shades — startColor on the from-square,
 *     endColor on the to-square (plus a border on the to-square). chessground
 *     normally uses a single `last-move` class for both squares, so we tag
 *     each square with our own class via `highlight.custom` (a SquareClasses
 *     Map) — see `lastMoveHighlight` in helpers.ts and the call sites in
 *     index.web.tsx.
 *   - selected-square highlight
 *   - king-in-check radial highlight (matches the package's red radial)
 *
 * Themes are activated by setting className `circlechess-theme-<name>` on the
 * cg-wrap container. Source colors mirror `CCColors.ChessBoard.*`.
 */
import CCColors from '@/Utils/CCColors';

type Theme = {
  className: string;
  light: string;
  dark: string;
  /** background color for the from-square of the last move */
  lastMoveFrom: string;
  /** background color for the to-square of the last move */
  lastMoveTo: string;
};

const THEMES: Theme[] = [
  {
    className: 'circlechess-theme-default',
    light: CCColors.ChessBoard.Light,
    // The package uses CCColors.Progress.Bar (== #D1AB41) as the default dark.
    // CCColors.ChessBoard.Dark is the same value with different case.
    dark: CCColors.ChessBoard.Dark,
    lastMoveFrom: CCColors.ChessBoard.StartColor,
    lastMoveTo: CCColors.ChessBoard.EndColor,
  },
  {
    className: 'circlechess-theme-green-white',
    light: CCColors.ChessBoard.GreenLightColor,
    dark: CCColors.ChessBoard.GreenDarkColor,
    lastMoveFrom: CCColors.ChessBoard.GreenStartColor,
    lastMoveTo: CCColors.ChessBoard.GreenEndColor,
  },
  {
    className: 'circlechess-theme-blue-white',
    light: CCColors.ChessBoard.BlueLightColor,
    dark: CCColors.ChessBoard.BlueDarkColor,
    lastMoveFrom: CCColors.ChessBoard.BlueStartColor,
    lastMoveTo: CCColors.ChessBoard.BlueEndColor,
  },
];

const CHECK_RADIAL = 'radial-gradient(transparent 60%, #EB5757)';

/**
 * Build an SVG data-URI that draws a 2×2 chequer tile. Tiled at 25%×25% of
 * cg-board, this produces an 8×8 board where a8 (top-left when white plays)
 * is light — the standard chess-board orientation.
 *
 * The tile is laid out as:
 *   [ light, dark  ]
 *   [ dark,  light ]
 * The light cells are TRANSPARENT in the SVG, so cg-board's `background-color`
 * shows through.
 */
function chequerBg(dark: string): string {
  const encDark = encodeURIComponent(dark);
  // Single quotes inside the SVG avoid having to encode double quotes.
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2 2' ` +
    `shape-rendering='crispEdges'>` +
    `<rect x='1' width='1' height='1' fill='${encDark}'/>` +
    `<rect y='1' width='1' height='1' fill='${encDark}'/>` +
    `</svg>`;
  return `url("data:image/svg+xml;utf8,${svg}")`;
}

// Legal-move destination indicators (the small green dot on an empty target,
// or a green ring around a capturable opponent piece). chessground emits
// `square.move-dest` and `square.oc.move-dest` (oc = "occupied") classes
// when a piece is selected and `movable.showDests` is true, but only ships
// the actual visual in its built-in brown/blue/green theme CSS — which we
// don't load. Same shape as lichess's brown theme so the dots match user
// expectation. Theme-independent (same green across all CC themes).
const MOVE_DEST_CSS = `
cg-board square.move-dest {
  background: radial-gradient(rgba(20, 85, 30, 0.5) 22%, #208530 0, rgba(0, 0, 0, 0.3) 0, rgba(0, 0, 0, 0) 0);
}
cg-board square.oc.move-dest {
  background: radial-gradient(transparent 0%, transparent 80%, rgba(20, 85, 0, 0.3) 80%);
}
cg-board square.move-dest:hover {
  background: rgba(20, 85, 30, 0.3);
}
cg-board square.premove-dest {
  background: radial-gradient(rgba(176, 30, 30, 0.5) 22%, #B01E1E 0, rgba(0, 0, 0, 0.3) 0, rgba(0, 0, 0, 0) 0);
}
cg-board square.oc.premove-dest {
  background: radial-gradient(transparent 0%, transparent 80%, rgba(176, 30, 30, 0.2) 80%);
}
cg-board square.premove-dest:hover {
  background: rgba(176, 30, 30, 0.2);
}
`;

// Premove highlights are theme-independent (red across all themes) — visually
// distinct from confirmed last-move highlights (yellow/green) and selected-
// square highlights (green tint). See enqueuePremove / buildHighlightMap in
// index.web.tsx for how these classes get assigned.
const PREMOVE_CSS = `
cg-board square.cc-premove-from {
  background-color: rgba(176, 30, 30, 0.4);
}
cg-board square.cc-premove-to {
  background-color: rgba(176, 30, 30, 0.4);
  box-shadow: inset 0 0 0 2px rgb(175, 60, 60);
}
`;

// Touch-and-move rule: when the user has "touched" a piece (PlayChess
// `restrictedPiece` is set), highlight that square with a green tint so the
// user remembers which piece they're locked into moving. Color matches the
// legacy chessboard-package (rgba(20, 85, 35, 0.3) on
// Chessboard.js:476-478). Placed AFTER premove CSS so on a square that has
// both classes (e.g. the user queued a premove of the restricted piece),
// the restricted highlight wins — that information is more important
// because it tells the user the move is mandatory.
const RESTRICTED_PIECE_CSS = `
cg-board square.cc-restricted-piece {
  background-color: rgba(20, 85, 35, 0.3);
}
`;

export const THEME_CSS =
  THEMES.map(
    t => `
.${t.className} cg-board {
  background-color: ${t.light};
  background-image: ${chequerBg(t.dark)};
  background-size: 25% 25%;
}
.${t.className} cg-board square.cc-last-move-from {
  background-color: ${t.lastMoveFrom};
}
.${t.className} cg-board square.cc-last-move-to {
  background-color: ${t.lastMoveTo};
  box-shadow: inset 0 0 0 2px rgb(120, 113, 64);
}
.${t.className} cg-board square.selected {
  background-color: rgba(20, 85, 30, 0.5);
}
.${t.className} cg-board square.check {
  background: ${CHECK_RADIAL};
}
`,
  ).join('\n') +
  MOVE_DEST_CSS +
  PREMOVE_CSS +
  RESTRICTED_PIECE_CSS;
